import React, { useState, useEffect, useCallback, useRef } from 'react';
import AdminAuth from '../services/AdminAuth';
import './ServerStatus.css';

const API_BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:5555' : '';
const POLL_INTERVAL = 15000; // 15 Sekunden
const DOWNTIME_STORAGE_KEY = 'serverDowntimeLog';

function ServerStatus() {
  const [health, setHealth] = useState(null);
  const [scheduler, setScheduler] = useState(null);
  const [isOnline, setIsOnline] = useState(null); // null = noch nicht geprüft
  const [lastCheck, setLastCheck] = useState(null);
  const [downtimeLog, setDowntimeLog] = useState([]);
  const [currentDownStart, setCurrentDownStart] = useState(null);
  const downtimeStartRef = useRef(null);

  // Downtime-Log aus localStorage laden
  useEffect(() => {
    try {
      const saved = localStorage.getItem(DOWNTIME_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Nur Einträge der letzten 7 Tage behalten
        const weekAgo = Date.now() - 7 * 86400000;
        const filtered = parsed.filter(e => new Date(e.end || e.start).getTime() > weekAgo);
        setDowntimeLog(filtered);
      }
    } catch (_) {}
  }, []);

  // Downtime-Log in localStorage speichern
  useEffect(() => {
    if (downtimeLog.length > 0) {
      localStorage.setItem(DOWNTIME_STORAGE_KEY, JSON.stringify(downtimeLog));
    }
  }, [downtimeLog]);

  const checkHealth = useCallback(async () => {
    const now = new Date();
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);

      const res = await fetch(`${API_BASE}/api/health`, { signal: controller.signal });
      clearTimeout(timeout);

      if (res.ok) {
        const data = await res.json();
        setHealth(data);
        setLastCheck(now);

        // War vorher offline → Downtime-Eintrag abschließen
        if (downtimeStartRef.current) {
          setDowntimeLog(prev => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last && !last.end) {
              last.end = now.toISOString();
              last.duration = Math.round((now.getTime() - new Date(last.start).getTime()) / 1000);
            }
            return updated;
          });
          downtimeStartRef.current = null;
          setCurrentDownStart(null);
        }

        setIsOnline(true);
      } else {
        throw new Error(`HTTP ${res.status}`);
      }
    } catch (err) {
      setHealth(null);
      setLastCheck(now);

      // Neuen Downtime-Eintrag starten
      if (!downtimeStartRef.current) {
        downtimeStartRef.current = now;
        setCurrentDownStart(now);
        setDowntimeLog(prev => [...prev, { start: now.toISOString(), end: null, duration: null }]);
      }

      setIsOnline(false);
    }
  }, []);

  const checkScheduler = useCallback(async () => {
    try {
      const token = AdminAuth.getToken();
      const res = await fetch(`${API_BASE}/api/admin/batch-scheduler/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setScheduler(data.scheduler || null);
      }
    } catch (_) {}
  }, []);

  // Polling starten
  useEffect(() => {
    checkHealth();
    checkScheduler();
    const interval = setInterval(() => {
      checkHealth();
      checkScheduler();
    }, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [checkHealth, checkScheduler]);

  // Downtime-Log löschen
  const clearDowntimeLog = () => {
    setDowntimeLog([]);
    localStorage.removeItem(DOWNTIME_STORAGE_KEY);
  };

  // Hilfsfunktionen
  const formatUptime = (seconds) => {
    if (!seconds) return '—';
    const d = Math.floor(seconds / 86400);
    const h = Math.floor((seconds % 86400) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (d > 0) return `${d}T ${h}h ${m}m`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'läuft...';
    if (seconds < 60) return `${seconds}s`;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    if (m < 60) return `${m}m ${s}s`;
    const h = Math.floor(m / 60);
    return `${h}h ${m % 60}m`;
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return '—';
    return new Date(isoStr).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  // Aktuelle Downtime-Dauer berechnen
  const currentDownDuration = currentDownStart
    ? Math.round((Date.now() - currentDownStart.getTime()) / 1000)
    : 0;

  // Gesamte Downtime der letzten 24h
  const totalDowntime24h = downtimeLog.reduce((sum, entry) => {
    const start = new Date(entry.start).getTime();
    const end = entry.end ? new Date(entry.end).getTime() : Date.now();
    const dayAgo = Date.now() - 86400000;
    if (end < dayAgo) return sum;
    const effectiveStart = Math.max(start, dayAgo);
    return sum + Math.round((end - effectiveStart) / 1000);
  }, 0);

  // Uptime-Prozent der letzten 24h
  const uptimePercent24h = totalDowntime24h > 0
    ? Math.max(0, 100 - (totalDowntime24h / 864)).toFixed(1)
    : isOnline !== null ? '100.0' : '—';

  return (
    <div className="server-status">
      <h2>🖥️ Server Status</h2>

      {/* Status-Karten */}
      <div className="status-cards">
        <div className={`status-card ${isOnline === null ? 'checking' : isOnline ? 'online' : 'offline'}`}>
          <div className="status-indicator">
            <span className={`status-dot ${isOnline === null ? 'pulse-gray' : isOnline ? 'pulse-green' : 'pulse-red'}`} />
            <span className="status-label">
              {isOnline === null ? 'Prüfe...' : isOnline ? 'Online' : 'Offline'}
            </span>
          </div>
          {isOnline === false && currentDownStart && (
            <div className="downtime-alert">
              ⚠️ Offline seit {formatDuration(currentDownDuration)}
            </div>
          )}
        </div>

        {health && (
          <>
            <div className="status-card info">
              <div className="card-label">Uptime</div>
              <div className="card-value">{formatUptime(health.uptime)}</div>
            </div>
            <div className="status-card info">
              <div className="card-label">Datenbank</div>
              <div className={`card-value ${health.database === 'connected' ? 'text-green' : 'text-red'}`}>
                {health.database === 'connected' ? '✅ Verbunden' : '❌ ' + health.database}
              </div>
            </div>
            <div className="status-card info">
              <div className="card-label">RAM</div>
              <div className="card-value">{health.memory ? Math.round(health.memory) + ' MB' : '—'}</div>
            </div>
          </>
        )}

        <div className="status-card info">
          <div className="card-label">Uptime (24h)</div>
          <div className={`card-value ${parseFloat(uptimePercent24h) >= 99 ? 'text-green' : parseFloat(uptimePercent24h) >= 95 ? 'text-yellow' : 'text-red'}`}>
            {uptimePercent24h}%
          </div>
        </div>
      </div>

      {/* Scheduler Status */}
      {scheduler && (
        <div className="scheduler-section">
          <h3>⏰ Batch Scraper Scheduler</h3>
          <div className="scheduler-info">
            <div className="scheduler-row">
              <span className="scheduler-label">Status:</span>
              <span className={scheduler.isRunning ? 'text-green' : 'text-red'}>
                {scheduler.isRunning ? '✅ Aktiv' : '❌ Inaktiv'}
              </span>
            </div>
            <div className="scheduler-row">
              <span className="scheduler-label">Zeitplan:</span>
              <span>{scheduler.schedule}</span>
            </div>
            <div className="scheduler-row">
              <span className="scheduler-label">Letzter Lauf:</span>
              <span>{scheduler.lastRunTime}</span>
            </div>
            <div className="scheduler-row">
              <span className="scheduler-label">Nächster Lauf:</span>
              <span>{scheduler.nextRunTime}</span>
            </div>
            <div className="scheduler-row">
              <span className="scheduler-label">Zeitzone:</span>
              <span>{scheduler.timezone}</span>
            </div>
          </div>
        </div>
      )}

      {/* Downtime-Log */}
      <div className="downtime-section">
        <div className="downtime-header">
          <h3>📉 Downtime-Log (letzte 7 Tage)</h3>
          {downtimeLog.length > 0 && (
            <button onClick={clearDowntimeLog} className="clear-btn">Log löschen</button>
          )}
        </div>

        {downtimeLog.length === 0 ? (
          <p className="no-downtime">✅ Keine Ausfälle aufgezeichnet</p>
        ) : (
          <table className="downtime-table">
            <thead>
              <tr>
                <th>Start</th>
                <th>Ende</th>
                <th>Dauer</th>
              </tr>
            </thead>
            <tbody>
              {[...downtimeLog].reverse().map((entry, i) => (
                <tr key={i} className={entry.end ? '' : 'active-downtime'}>
                  <td>{formatTime(entry.start)}</td>
                  <td>{entry.end ? formatTime(entry.end) : <span className="pulse-text">jetzt</span>}</td>
                  <td>{entry.duration ? formatDuration(entry.duration) : <span className="pulse-text">{formatDuration(currentDownDuration)}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {totalDowntime24h > 0 && (
          <div className="downtime-summary">
            Gesamt-Downtime (24h): <strong>{formatDuration(totalDowntime24h)}</strong>
          </div>
        )}
      </div>

      {/* Last Check */}
      {lastCheck && (
        <div className="last-check">
          Letzte Prüfung: {lastCheck.toLocaleTimeString('de-DE')} · Intervall: {POLL_INTERVAL / 1000}s
        </div>
      )}
    </div>
  );
}

export default ServerStatus;
